/**
 * KAN-713: карточка прокси справочника: создать или изменить. Строка провайдера
 * раскладывается по полям тем же разбором, что в формах аккаунта (KAN-655).
 * "Проверить" проверяет без сохранения и показывает результат здесь же.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ConnectSellerFields } from '@/components/telegram/ConnectSellerFields';
import { ProxyCheckBadge } from '@/components/telegram/ProxyCheckBadge';
import { ProxyFields } from '@/components/telegram/ProxyFields';
import { useTelegramProxyMutations } from '@/hooks/useTelegramProxies';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { PROXY_PASTE_HINT, parseProxyString } from '@/lib/telegramProxy';
import {
  DIRECTORY_PROXY_TYPES,
  EMPTY_DIRECTORY_FORM,
  PROXY_COUNTRY_MAX,
  PROXY_LABEL_MAX,
  PROXY_NOTES_MAX,
  checksSavedProxy,
  connectionBody,
  directoryBody,
  formFromProxy,
  validateDirectoryConnection,
  validateDirectoryCost,
  type DirectoryFormValues,
} from '@/lib/telegramProxyDirectory';
import { telegramProxiesApi } from '@/services/api/telegramProxies';
import type { ProxyCheckResult, TelegramProxy } from '@/types/telegramProxy';

const STRING_NOT_PARSED = 'Строку не удалось разобрать, заполните поля ниже';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Карточка для изменения; нет: создание. */
  proxy?: TelegramProxy | null;
}

export function ProxyDirectoryDialog({ open, onOpenChange, proxy }: Props): JSX.Element {
  const { create, update } = useTelegramProxyMutations();
  const [form, setForm] = useState<DirectoryFormValues>(EMPTY_DIRECTORY_FORM);
  const [raw, setRaw] = useState('');
  const [rawProblem, setRawProblem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<ProxyCheckResult | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(proxy ? formFromProxy(proxy) : EMPTY_DIRECTORY_FORM);
    setRaw('');
    setRawProblem(null);
    setError(null);
    setCheck(null);
  }, [open, proxy]);

  const editing = !!proxy;
  const saving = create.isPending || update.isPending;
  const busy = saving || checking;

  const patch = (p: Partial<DirectoryFormValues>): void => {
    setForm((f) => ({ ...f, ...p }));
    setCheck(null);
  };

  const changeRaw = (text: string): void => {
    setRaw(text);
    if (!text.trim()) {
      setRawProblem(null);
      return;
    }
    const parsed = parseProxyString(text);
    setRawProblem(parsed ? null : STRING_NOT_PARSED);
    if (parsed) patch({ proxy: { ...form.proxy, ...parsed } });
  };

  const runCheck = async (): Promise<void> => {
    const problem = validateDirectoryConnection(form);
    setError(problem);
    if (problem) return;
    setChecking(true);
    try {
      const result =
        proxy && checksSavedProxy(form, proxy)
          ? await telegramProxiesApi.checkSaved(proxy.id)
          : await telegramProxiesApi.checkDraft(connectionBody(form));
      setCheck(result);
    } catch (err) {
      setError(extractErrorMessage(err, 'Не удалось проверить прокси'));
    } finally {
      setChecking(false);
    }
  };

  const submit = async (): Promise<void> => {
    const problem = validateDirectoryConnection(form) ?? validateDirectoryCost(form);
    setError(problem);
    if (problem) return;
    try {
      const body = directoryBody(form);
      if (proxy) await update.mutateAsync({ id: proxy.id, data: body });
      else await create.mutateAsync(body);
      onOpenChange(false);
    } catch (err) {
      setError(extractErrorMessage(err, 'Не удалось сохранить прокси'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid="proxy-dir-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Прокси: изменить' : 'Новый прокси'}</DialogTitle>
          <DialogDescription>
            Прокси выбирается из справочника при подключении аккаунта и в диалоге смены прокси.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="proxy-dir-string">Строка от провайдера</Label>
            <Input
              id="proxy-dir-string"
              data-testid="proxy-dir-string"
              autoComplete="off"
              placeholder="socks5://логин:пароль@адрес:порт"
              value={raw}
              disabled={busy}
              onChange={(e) => changeRaw(e.target.value)}
            />
            <p className="text-xs text-gray-500">{PROXY_PASTE_HINT}</p>
            {rawProblem && (
              <p className="text-xs text-amber-700" data-testid="proxy-dir-string-problem">
                {rawProblem}
              </p>
            )}
          </div>
          <ProxyFields
            idPrefix="proxy-dir"
            values={form.proxy}
            types={DIRECTORY_PROXY_TYPES}
            disabled={busy}
            onChange={(p) => patch({ proxy: { ...form.proxy, ...p } })}
          />
          {editing && proxy?.has_password && (
            <p className="text-xs text-gray-500">
              Пароль сохранён. Оставьте поле пустым, чтобы не менять его.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
              <Label htmlFor="proxy-dir-label">Название</Label>
              <Input
                id="proxy-dir-label"
                data-testid="proxy-dir-label"
                autoComplete="off"
                maxLength={PROXY_LABEL_MAX}
                placeholder="Например, NL-1"
                value={form.label}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
              <Label htmlFor="proxy-dir-country">Страна</Label>
              <Input
                id="proxy-dir-country"
                data-testid="proxy-dir-country"
                autoComplete="off"
                maxLength={PROXY_COUNTRY_MAX}
                placeholder="Например, NL"
                value={form.country}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="proxy-dir-cost">Стоимость в месяц, ₽</Label>
            <Input
              id="proxy-dir-cost"
              data-testid="proxy-dir-cost"
              inputMode="decimal"
              autoComplete="off"
              placeholder="Например, 500"
              value={form.monthlyCost}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, monthlyCost: e.target.value }))}
            />
          </div>
          <ConnectSellerFields
            sellerId={form.sellerId}
            disabled={busy}
            onChange={(sellerId) => setForm((f) => ({ ...f, sellerId }))}
          />
          <div className="flex flex-col gap-1">
            <Label htmlFor="proxy-dir-notes">Заметка</Label>
            <Textarea
              id="proxy-dir-notes"
              data-testid="proxy-dir-notes"
              rows={2}
              maxLength={PROXY_NOTES_MAX}
              value={form.notes}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
          {check && <ProxyCheckBadge result={check} testId="proxy-dir-check-result" />}
          {error && (
            <p className="text-xs text-red-700" data-testid="proxy-dir-error">
              {error}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Отмена
          </Button>
          <Button
            variant="outline"
            data-testid="proxy-dir-check"
            disabled={busy}
            onClick={runCheck}
          >
            {checking ? 'Проверка...' : 'Проверить'}
          </Button>
          <Button data-testid="proxy-dir-submit" disabled={busy} onClick={submit}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
